import React, { useState, useEffect } from 'react';
import { useNotification } from '../context/NotificationContext';
import './UserFormModal.css';
import { getAuthHeader } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL;

function UserFormModal({ isOpen, onClose, onUserSaved, editingUser, compSets }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [roleId, setRoleId] = useState('');
    const [defaultCompSetId, setDefaultCompSetId] = useState('');
    const [receivesAlerts, setReceivesAlerts] = useState(false);
    const [alertPreference, setAlertPreference] = useState('ALL_HOTELS');

    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);

    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showNotification } = useNotification();

    // Fetch roles dynamically from the API
    useEffect(() => {
        if (!isOpen) return;
        const fetchRoles = async () => {
            setRolesLoading(true);
            try {
                const res = await fetch(`${API_URL}/api.php?path=roles`, { headers: getAuthHeader() });
                const data = await res.json();
                if (data.success) {
                    setRoles(data.data);
                    // Set default role_id if not editing
                    if (!editingUser && data.data.length > 0) {
                        // Default to first non-Super Admin role
                        const defaultRole = data.data.find(r => r.name !== 'Super Admin') || data.data[0];
                        setRoleId(String(defaultRole.role_id));
                    }
                }
            } catch (e) {
                console.error('Error fetching roles:', e);
            } finally {
                setRolesLoading(false);
            }
        };
        fetchRoles();
    }, [isOpen, editingUser]);

    // Populate form when editing
    useEffect(() => {
        if (isOpen) {
            if (editingUser) {
                setName(editingUser.name || '');
                setEmail(editingUser.email || '');
                setRoleId(editingUser.role_id ? String(editingUser.role_id) : '');
                setDefaultCompSetId(editingUser.default_compset_id || '');
                const receivesAlertsBool = !!editingUser.receives_alerts;
                setReceivesAlerts(receivesAlertsBool);
                setAlertPreference(
                    (receivesAlertsBool && editingUser.alert_preference_type)
                        ? editingUser.alert_preference_type
                        : 'ALL_HOTELS'
                );
                setPassword('');
            } else {
                setName('');
                setEmail('');
                setPassword('');
                setDefaultCompSetId('');
                setReceivesAlerts(false);
                setAlertPreference('ALL_HOTELS');
            }
            setError('');
        }
    }, [editingUser, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || (!editingUser && !password.trim())) {
            setError('Nombre, email y contraseña (nuevo usuario) son obligatorios.');
            showNotification('Nombre, email y contraseña (nuevo usuario) son obligatorios.', 'warning');
            return;
        }
        if (!roleId) {
            setError('Debes seleccionar un papel para el usuario.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        const isEditMode = !!editingUser;
        const path = isEditMode ? `users/${editingUser.user_id}` : 'users';
        const url  = `${API_URL}/api.php?path=${path}`;

        // Find the role name for backwards compatibility with user_level
        const selectedRole = roles.find(r => String(r.role_id) === String(roleId));

        const body = {
            name:                name.trim(),
            email:               email.trim(),
            role_id:             parseInt(roleId, 10),
            user_level:          selectedRole ? selectedRole.name : '',  // keep in sync
            default_compset_id:  defaultCompSetId ? parseInt(defaultCompSetId, 10) : null,
            receives_alerts:     receivesAlerts ? 1 : 0,
            alert_preference_type: receivesAlerts ? alertPreference : 'NONE',
        };
        if (password.trim() !== '') { body.password = password; }

        try {
            const response = await fetch(url, {
                method:  isEditMode ? 'PUT' : 'POST',
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                body:    JSON.stringify(body),
            });

            const resultText = await response.text();
            let result;
            try { result = JSON.parse(resultText); }
            catch (parseError) { throw new Error(`Respuesta del servidor no es JSON: ${resultText.substring(0, 100)}`); }

            if (response.ok && result.success) {
                showNotification(result.data?.message || (isEditMode ? '¡Usuario actualizado!' : '¡Usuario creado!'), 'success');
                if (onUserSaved) onUserSaved();
                onClose();
            } else {
                throw new Error(result.error?.message || 'Error del servidor.');
            }
        } catch (err) {
            console.error('[UserFormModal] Submit error:', err);
            setError(err.message);
            showNotification(`Error: ${err.message}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>{editingUser ? 'Editar Usuario' : 'Agregar Nuevo Usuario'}</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>
                <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                        {error && <p className="error-message" role="alert">{error}</p>}

                        <div className="form-group">
                            <label htmlFor="userform-name">Nombre</label>
                            <input type="text" id="userform-name" value={name} onChange={(e) => setName(e.target.value)} required />
                        </div>

                        <div className="form-group">
                            <label htmlFor="userform-email">Email</label>
                            <input type="email" id="userform-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                        </div>

                        <div className="form-group">
                            <label htmlFor="userform-password">Contraseña</label>
                            <input
                                type="password"
                                id="userform-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={editingUser ? 'Dejar en blanco para no cambiar' : ''}
                            />
                        </div>

                        {/* DYNAMIC ROLE DROPDOWN */}
                        <div className="form-group">
                            <label htmlFor="userform-role">Papel del Usuario</label>
                            {rolesLoading ? (
                                <p style={{ fontSize: '13px', color: '#666' }}>Cargando papeles...</p>
                            ) : (
                                <select
                                    id="userform-role"
                                    value={roleId}
                                    onChange={(e) => setRoleId(e.target.value)}
                                >
                                    <option value="">-- Seleccionar papel --</option>
                                    {roles.map(role => (
                                        <option key={role.role_id} value={role.role_id}>
                                            {role.name}
                                            {role.description ? ` — ${role.description}` : ''}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="userform-defaultCompSet">Comp Set Predeterminado (Opcional)</label>
                            <select
                                id="userform-defaultCompSet"
                                value={defaultCompSetId}
                                onChange={(e) => setDefaultCompSetId(e.target.value)}
                            >
                                <option value="">Ninguno</option>
                                {(compSets && Array.isArray(compSets))
                                    ? compSets.map(set => (
                                        <option key={set.comp_set_id} value={set.comp_set_id}>
                                            {set.name}
                                        </option>
                                    ))
                                    : null}
                            </select>
                        </div>

                        <div className="form-group form-group-checkbox">
                            <input
                                type="checkbox"
                                id="userform-receivesAlerts"
                                checked={receivesAlerts}
                                onChange={(e) => setReceivesAlerts(e.target.checked)}
                            />
                            <label htmlFor="userform-receivesAlerts">Recibir Alertas de Datos Faltantes por Email</label>
                        </div>

                        {receivesAlerts && (
                            <div className="form-group alert-preference-group">
                                <label>Tipo de Alerta a Recibir:</label>
                                <div className="radio-option">
                                    <input type="radio" id="alertTypeAll" name="alertPreference" value="ALL_HOTELS"
                                        checked={alertPreference === 'ALL_HOTELS'}
                                        onChange={(e) => setAlertPreference(e.target.value)} />
                                    <label htmlFor="alertTypeAll">Todos los hoteles (general)</label>
                                </div>
                                <div className="radio-option">
                                    <input type="radio" id="alertTypeCompset" name="alertPreference" value="DEFAULT_COMPSET"
                                        checked={alertPreference === 'DEFAULT_COMPSET'}
                                        onChange={(e) => setAlertPreference(e.target.value)}
                                        disabled={!defaultCompSetId} />
                                    <label htmlFor="alertTypeCompset">
                                        Solo hoteles en mi compset predeterminado
                                        {!defaultCompSetId && <span className="field-hint"> (Seleccione un compset primero)</span>}
                                    </label>
                                </div>
                                <div className="radio-option">
                                    <input type="radio" id="alertTypeCompsetPropio" name="alertPreference" value="DEFAULT_COMPSET_PROPIO"
                                        checked={alertPreference === 'DEFAULT_COMPSET_PROPIO'}
                                        onChange={(e) => setAlertPreference(e.target.value)}
                                        disabled={!defaultCompSetId} />
                                    <label htmlFor="alertTypeCompsetPropio">
                                        Solo hoteles propios en mi compset predeterminado
                                        {!defaultCompSetId && <span className="field-hint"> (Seleccione un compset primero)</span>}
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="modal-footer">
                            <button type="button" className="cancel-btn" onClick={onClose} disabled={isSubmitting}>
                                Cancelar
                            </button>
                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'Guardando...' : (editingUser ? 'Actualizar Usuario' : 'Crear Usuario')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default UserFormModal;
